import { ReactNode } from "react";
import { UseFormRegister, FieldErrors } from "react-hook-form";
import { Input } from "@/components/common/Input";
import { Label } from "@/components/common/FormLabel";
import { ErrorLabel } from "@/components/common/ErrorLabel";

type AddressFieldProps = {
  /** Prefix applied to all HTML id attributes. Default: "" */
  idPrefix?: string;
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  isMandatory?: boolean;
  isReadOnly?: boolean;
  /**
   * Optional custom header rendered above the first input.
   * When provided it replaces the built-in "Address" label row.
   */
  header?: ReactNode;
};

export const AddressField = ({
  idPrefix = "",
  register,
  errors,
  isMandatory = true,
  isReadOnly = false,
  header,
}: AddressFieldProps) => {
  const id = (name: string) => `${idPrefix}${name}`;
  const addr1Rules = isMandatory
    ? { required: "Address is required", maxLength: { value: 50, message: "Address must be 50 characters or less" } }
    : { maxLength: { value: 50, message: "Address must be 50 characters or less" } };

  return (
    <div className="space-y-3">
      <div>
        {header ?? (
          <Label
            forField={id("addr1")}
            label={isMandatory ? <>Address <span className="text-red-500">*</span></> : "Address"}
          />
        )}
        <Input
          id={id("addr1")}
          type="text"
          maxLength={50}
          placeholder="Address line 1"
          disabled={isReadOnly}
          inErrorState={Boolean(errors["address1"])}
          {...register("address1", addr1Rules)}
        />
        <ErrorLabel error={errors["address1"]?.message as string | undefined} />
      </div>
      <div>
        <Input
          id={id("addr2")}
          type="text"
          maxLength={50}
          placeholder="Address line 2"
          disabled={isReadOnly}
          {...register("address2")}
        />
      </div>
      <div>
        <Input
          id={id("addr3")}
          type="text"
          maxLength={50}
          placeholder="Address line 3"
          disabled={isReadOnly}
          {...register("address3")}
        />
      </div>
      <div>
        <Input
          id={id("addr4")}
          type="text"
          maxLength={50}
          placeholder="Address line 4"
          disabled={isReadOnly}
          {...register("address4")}
        />
      </div>
      <div>
        <Label forField={id("town")} label="Town" />
        <Input
          id={id("town")}
          type="text"
          maxLength={50}
          disabled={isReadOnly}
          {...register("town")}
        />
      </div>
      <div>
        <Label forField={id("county")} label="County" />
        <Input
          id={id("county")}
          type="text"
          maxLength={50}
          disabled={isReadOnly}
          {...register("county")}
        />
      </div>
      <div>
        <Label forField={id("country")} label="Country" />
        <Input
          id={id("country")}
          type="text"
          maxLength={50}
          disabled={isReadOnly}
          {...register("country")}
        />
      </div>
      <div>
        <Label forField={id("postcode")} label={isMandatory ? <>Post Code <span className="text-red-500">*</span></> : "Post Code"} />
        <Input
          id={id("postcode")}
          type="text"
          maxLength={12}
          disabled={isReadOnly}
          inErrorState={Boolean(errors["postcode"])}
          {...register("postcode", isMandatory ? { required: "Post code is required" } : {})}
        />
        <ErrorLabel error={errors["postcode"]?.message as string | undefined} />
      </div>
    </div>
  );
};
